import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  Search,
  X,
  Trash2,
  Loader2,
  ImageIcon,
  AlertTriangle,
  Check,
} from "lucide-react";

/** Shape returned by GET /api/page-assets and POST /api/pages/upload-image. */
export interface PageAsset {
  id: string;
  tenantId: string;
  storageKey: string;
  cdnUrl: string;
  filename: string;
  altText: string | null;
  fileSize: number;
  width: number | null;
  height: number | null;
  createdAt: string;
}

interface PageReference {
  pageId: string;
  title: string;
  status: string;
  inDraft: boolean;
  inPublished: boolean;
}

interface MediaLibraryProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user selects an image from the library. */
  onSelect: (cdnUrl: string, altText: string | null) => void;
}

/**
 * Media library modal -- shows all tenant page images in a grid.
 * Supports search, alt text editing, deletion with usage awareness,
 * and selection for insertion into an image field.
 */
export function MediaLibrary({ open, onClose, onSelect }: MediaLibraryProps) {
  const [assets, setAssets] = useState<PageAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingAlt, setEditingAlt] = useState<string>("");
  const [savingAlt, setSavingAlt] = useState(false);
  const [altSaved, setAltSaved] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteRefs, setDeleteRefs] = useState<PageReference[] | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch assets when the modal opens or search changes
  const fetchAssets = useCallback(async (searchTerm: string) => {
    setLoading(true);
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const res = await fetch(`/api/page-assets${params}`);
      const contentType = res.headers.get("content-type") ?? "";
      if (res.ok && contentType.includes("application/json")) {
        const data = (await res.json()) as PageAsset[];
        setAssets(data);
        setLoadError(false);
      } else {
        // Non-JSON (e.g. a proxy misroute returning HTML) or an API error --
        // surface it instead of masquerading as an empty library
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedId(null);
      setDeleteRefs(null);
      setDeleteConfirmId(null);
      void fetchAssets("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open, fetchAssets]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      void fetchAssets(search);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search, open, fetchAssets]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedId) {
          setSelectedId(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose, selectedId]);

  // Prevent body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const selectedAsset = selectedId
    ? assets.find((a) => a.id === selectedId) ?? null
    : null;

  // When selection changes, load alt text for editing
  useEffect(() => {
    if (selectedAsset) {
      setEditingAlt(selectedAsset.altText ?? "");
      setAltSaved(false);
    }
  }, [selectedAsset]);

  const handleSaveAlt = useCallback(async () => {
    if (!selectedAsset) return;
    setSavingAlt(true);
    try {
      const res = await fetch(`/api/page-assets/${selectedAsset.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ altText: editingAlt || null }),
      });
      if (res.ok) {
        const updated = (await res.json()) as PageAsset;
        setAssets((prev) =>
          prev.map((a) => (a.id === updated.id ? updated : a)),
        );
        setAltSaved(true);
        setTimeout(() => setAltSaved(false), 2000);
      }
    } catch {
      // ignore
    } finally {
      setSavingAlt(false);
    }
  }, [selectedAsset, editingAlt]);

  const handleDelete = useCallback(
    async (assetId: string, confirm: boolean) => {
      setDeleting(assetId);
      try {
        const params = confirm ? "?confirm=true" : "";
        const res = await fetch(`/api/page-assets/${assetId}${params}`, {
          method: "DELETE",
        });
        if (res.status === 409) {
          // Asset is referenced -- show warning
          const data = (await res.json()) as {
            referencedIn: PageReference[];
            message: string;
          };
          setDeleteRefs(data.referencedIn);
          setDeleteConfirmId(assetId);
        } else if (res.ok) {
          setAssets((prev) => prev.filter((a) => a.id !== assetId));
          if (selectedId === assetId) setSelectedId(null);
          setDeleteRefs(null);
          setDeleteConfirmId(null);
        }
      } catch {
        // ignore
      } finally {
        setDeleting(null);
      }
    },
    [selectedId],
  );

  const handleSelect = useCallback(
    (asset: PageAsset) => {
      onSelect(asset.cdnUrl, asset.altText);
      onClose();
    },
    [onSelect, onClose],
  );

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Media library"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm motion-safe:animate-[fadein_0.12s_ease]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-paper-hi rounded-[14px] shadow-lift w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col overflow-hidden motion-safe:animate-[rise_0.15s_cubic-bezier(0.3,1.2,0.4,1)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 hair-b shrink-0">
          <h2 className="font-display text-[16px] font-semibold">
            Media library
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 hair-b shrink-0">
          <div className="relative">
            <Search
              size={14}
              strokeWidth={2}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-5"
            />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by filename..."
              className="w-full pl-8 pr-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
            />
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {loading && assets.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2
                size={24}
                strokeWidth={2}
                className="text-tide animate-spin"
              />
            </div>
          ) : loadError && assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-rust">
              <ImageIcon size={32} strokeWidth={1.2} className="mb-3 opacity-50" />
              <p className="text-[14px] font-medium mb-1">
                Couldn't load your images
              </p>
              <p className="text-[12px]">Close the library and try again.</p>
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-ink-4">
              <ImageIcon size={32} strokeWidth={1.2} className="mb-3 opacity-50" />
              <p className="text-[14px] font-medium mb-1">No images yet</p>
              <p className="text-[12px] text-ink-5">
                Upload images from any image field to see them here.
              </p>
            </div>
          ) : (
            <div className="flex gap-4">
              {/* Grid */}
              <div
                className={`grid gap-3 ${selectedAsset ? "w-2/3 grid-cols-3" : "w-full grid-cols-4"}`}
              >
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setSelectedId(
                        selectedId === asset.id ? null : asset.id,
                      )
                    }
                    onDoubleClick={() => handleSelect(asset)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedId(
                          selectedId === asset.id ? null : asset.id,
                        );
                      }
                    }}
                    className={`group relative border rounded-[10px] overflow-hidden text-left transition-all cursor-pointer ${
                      selectedId === asset.id
                        ? "border-tide ring-2 ring-tide/30"
                        : "border-ink-6 hover:border-ink-4"
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-square bg-canvas relative">
                      <img
                        src={asset.cdnUrl}
                        alt={asset.altText || asset.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Delete button (top-right on hover) */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(asset.id, false);
                        }}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-paper-hi/80 backdrop-blur-sm flex items-center justify-center text-ink-4 hover:text-rust hover:bg-paper-hi opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={`Delete ${asset.filename}`}
                      >
                        {deleting === asset.id ? (
                          <Loader2
                            size={12}
                            strokeWidth={2}
                            className="animate-spin"
                          />
                        ) : (
                          <Trash2 size={12} strokeWidth={2} />
                        )}
                      </button>
                    </div>
                    {/* Info bar */}
                    <div className="px-2 py-1.5">
                      <p className="text-[10.5px] font-medium text-ink-3 truncate">
                        {asset.filename}
                      </p>
                      <p className="text-[9.5px] text-ink-5">
                        {asset.width && asset.height
                          ? `${asset.width} x ${asset.height}`
                          : formatBytes(asset.fileSize)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Detail panel (right side) */}
              {selectedAsset && (
                <div className="w-1/3 border border-ink-6 rounded-[10px] p-3.5 flex flex-col gap-3 self-start">
                  {/* Preview */}
                  <div className="aspect-video bg-canvas rounded-[8px] overflow-hidden">
                    <img
                      src={selectedAsset.cdnUrl}
                      alt={selectedAsset.altText || selectedAsset.filename}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Filename */}
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-4 mb-1">
                      Filename
                    </p>
                    <p className="text-[12px] text-ink-2 font-mono break-all">
                      {selectedAsset.filename}
                    </p>
                  </div>

                  {/* Dimensions & Size */}
                  <div className="flex gap-4">
                    {selectedAsset.width && selectedAsset.height && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-4 mb-1">
                          Dimensions
                        </p>
                        <p className="text-[12px] text-ink-2">
                          {selectedAsset.width} x {selectedAsset.height}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-4 mb-1">
                        Size
                      </p>
                      <p className="text-[12px] text-ink-2">
                        {formatBytes(selectedAsset.fileSize)}
                      </p>
                    </div>
                  </div>

                  {/* Alt text */}
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-4 mb-1">
                      Alt text
                    </p>
                    {!selectedAsset.altText && editingAlt === "" && (
                      <p className="text-[11px] text-spark mb-1.5">
                        Add alt text for accessibility
                      </p>
                    )}
                    <textarea
                      value={editingAlt}
                      onChange={(e) => {
                        setEditingAlt(e.target.value);
                        setAltSaved(false);
                      }}
                      onKeyDown={(e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void handleSaveAlt();
                        }
                      }}
                      placeholder="Describe the image..."
                      rows={2}
                      className="w-full px-2.5 py-2 text-[12px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5 resize-none"
                    />
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        type="button"
                        onClick={() => void handleSaveAlt()}
                        disabled={
                          savingAlt ||
                          editingAlt === (selectedAsset.altText ?? "")
                        }
                        className="text-[11px] font-semibold text-tide px-2 py-1 rounded-[6px] hover:bg-tide-ss transition-colors disabled:opacity-40 disabled:cursor-default"
                      >
                        {savingAlt ? "Saving..." : "Save"}
                      </button>
                      {altSaved && (
                        <span className="text-[11px] text-grove flex items-center gap-1">
                          <Check size={12} strokeWidth={2} />
                          Saved
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Insert button */}
                  <button
                    type="button"
                    onClick={() => handleSelect(selectedAsset)}
                    className="w-full mt-1 px-3 py-2 rounded-[8px] bg-tide text-paper-hi text-[13px] font-semibold hover:bg-tide-d transition-colors"
                  >
                    Insert image
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Delete confirmation overlay */}
        {deleteConfirmId && deleteRefs && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-ink/30 backdrop-blur-sm rounded-[14px]">
            <div className="bg-paper-hi border border-ink-6 rounded-[14px] p-5 shadow-lift w-[360px]">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle
                  size={18}
                  strokeWidth={2}
                  className="text-spark"
                />
                <h4 className="text-[15px] font-semibold">Image in use</h4>
              </div>
              <p className="text-[13px] text-ink-3 leading-relaxed mb-3">
                This image is used on the following pages. Deleting it will
                leave broken images:
              </p>
              <ul className="mb-4 space-y-1">
                {deleteRefs.map((ref) => (
                  <li
                    key={ref.pageId}
                    className="text-[12.5px] text-ink-2 flex items-center gap-1.5"
                  >
                    <span className="font-medium">{ref.title}</span>
                    {ref.inPublished && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-grove bg-grove/10 px-1.5 py-0.5 rounded">
                        Published
                      </span>
                    )}
                    {ref.inDraft && !ref.inPublished && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-spark bg-spark/10 px-1.5 py-0.5 rounded">
                        Draft
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteRefs(null);
                    setDeleteConfirmId(null);
                  }}
                  className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-ink-6 bg-paper-hi text-ink-2 hover:bg-paper-2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(deleteConfirmId, true)}
                  className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 border border-rust bg-rust text-white hover:bg-rust-d transition-colors"
                >
                  Delete anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
